import {
	type Attachment,
	formatIstDateTime,
	formatVettingId,
	parseTimestamp,
	workStatusLabel,
} from "@anthra/platform/domain"
import { isAccessDenied, toDisplayable } from "@anthra/platform/errors"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { AccessDenied } from "@anthra/ui/feedback"
import { ExpandableRichText } from "@anthra/ui/rich-text"
import { ScrollFab } from "@anthra/ui/scroll-fab"
import { Skeleton, SkeletonText } from "@anthra/ui/skeleton"
import { createFileRoute, Link } from "@tanstack/react-router"
import { InfoIcon, TriangleAlertIcon } from "lucide-react"
import { useEffect } from "react"

import { AttachmentList } from "@/components/attachment-list"
import { useMarkSeen } from "@/features/activity/use-activity"
import { FeedbackPrompt as VettingFeedbackPrompt } from "@/features/vetting/feedback-prompt"
import { useVettingRequest } from "@/features/vetting/use-vetting"
import { VettingFollowUpComposer } from "@/features/vetting/vetting-follow-up-composer"
import { VettingThread } from "@/features/vetting/vetting-thread"

/**
 * One vetting request (US-00-004.10/.11/.12/.13).
 *
 * Rebuilt to match /queries/$queryId's layout — one column, the notes reading
 * like a query's description, the notice/draft as thread-style attachment
 * chips, and no separate facts rail: vetting now carries the same conversation
 * mechanics as a query, so the two screens read the same way.
 *
 * NOTHING ON THIS SCREEN CAN BE CHANGED. There is no control to edit the title,
 * the category, the notes or either upload, and the API exposes no route for it
 * (US-00-004.12) — the absence is the feature.
 *
 * The Expert's feedback appears only once the review is posted: on an Open or In
 * Progress request the detail endpoint carries no feedback content at all, so
 * this screen shows the awaiting-review placeholder rather than hiding something
 * it was given (US-00-004.11).
 */
export const Route = createFileRoute("/_app/vetting/$vettingId")({
	component: VettingDetail,
})

/**
 * A thread opens at its top, the question and the first messages, so the
 * conversation reads from the start (client feedback; it used to open at
 * its latest message — the composer is home.
 * Runs once the real content has painted, not the skeleton, so it scrolls to
 * the actual bottom rather than a placeholder's. Mirrors
 * `_app.queries.$queryId.tsx`'s own hook exactly.
 */
function useScrollToLatestOnLoad(ready: boolean, key: string): void {
	useEffect(() => {
		if (!ready) return undefined
		// Deferred a tick: the router's own navigation-complete handler resets
		// scroll to the top AFTER this effect would otherwise run, undoing it.
		// Running one macrotask later lands after that reset.
		const timer = setTimeout(() => {
			window.scrollTo({ top: 0 })
		}, 0)
		return () => {
			clearTimeout(timer)
		}
	}, [ready, key])
}

function VettingDetail() {
	const { vettingId } = Route.useParams()
	const request = useVettingRequest(vettingId)
	useMarkSeen("vetting", vettingId, request.isSuccess)
	useScrollToLatestOnLoad(request.isSuccess, vettingId)

	// Shaped like query-detail's own skeleton: a head, then two content blocks
	// (documents, thread) in one column — no rail, no timeline.
	if (request.isPending) {
		return (
			<div className="flex flex-col gap-6">
				<p
					role="status"
					className="sr-only"
				>
					Loading your vetting request.
				</p>
				<div
					className="flex flex-col gap-6"
					aria-hidden="true"
				>
					<div className="border-border flex flex-wrap items-start justify-between gap-4 border-b pb-6">
						<div className="flex min-w-0 flex-1 flex-col gap-3">
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-8 w-3/4" />
							<SkeletonText lines={2} />
							<div className="flex flex-wrap gap-3">
								<Skeleton className="h-6 w-24 rounded-sm" />
								<Skeleton className="h-4 w-40" />
							</div>
						</div>
						<Skeleton className="rounded-pill h-7 w-24 shrink-0" />
					</div>
					<div className="flex flex-col gap-4">
						{["notice", "draft"].map(key => (
							<div
								key={key}
								className="flex flex-wrap items-center gap-3"
							>
								<Skeleton className="h-11 flex-1" />
							</div>
						))}
					</div>
					<div className="flex flex-col gap-4">
						<Skeleton className="h-24 w-full" />
						<Skeleton className="h-24 w-3/4" />
					</div>
				</div>
			</div>
		)
	}

	if (request.isError) {
		// Somebody else's #V id, guessed or incremented, and one that never
		// existed — refused identically (US-00-004.16, US-00-006.18/.19).
		if (isAccessDenied(request.error)) {
			return (
				<AccessDenied
					backTo={
						<Link
							to="/vetting"
							className="text-primary text-sm underline"
						>
							Back to My Vetting Requests
						</Link>
					}
				/>
			)
		}

		const displayable = toDisplayable(request.error)
		return (
			<div className="flex flex-col gap-4">
				<div className="border-border flex flex-col gap-2 border-b pb-6">
					<h2 className="text-card-title text-foreground min-w-0 break-all">
						{displayable.title}
					</h2>
					<p className="text-description text-subtle-foreground">{displayable.message}</p>
				</div>
				<div
					className="border-danger-border bg-danger-soft flex items-start gap-3 rounded-lg border p-5"
					role="alert"
				>
					<TriangleAlertIcon
						className="text-danger mt-0.5 size-5 shrink-0"
						aria-hidden="true"
					/>
					<div className="flex flex-col gap-2">
						<h3 className="text-card-title text-foreground">
							Couldn&rsquo;t load this request
						</h3>
						<p className="text-description text-subtle-foreground">
							{displayable.message}
						</p>
						{displayable.canRetry ? (
							<div className="flex flex-wrap gap-2 pt-1">
								<button
									type="button"
									className={cn(buttonVariants({ variant: "secondary" }))}
									onClick={() => {
										void request.refetch()
									}}
								>
									Try again
								</button>
							</div>
						) : null}
					</div>
				</div>
			</div>
		)
	}

	const detail = request.data
	const submitted = parseTimestamp(detail.submittedAt)
	const reviewed = parseTimestamp(detail.reviewedAt)
	const documents: Attachment[] = [detail.notice, detail.draft].filter(
		(document): document is Attachment => document !== null,
	)

	return (
		<div className="flex flex-col gap-6">
			<ScrollFab />

			<Breadcrumb>
				<BreadcrumbItem>
					<Link
						to="/vetting"
						className="hover:text-foreground underline-offset-2 hover:underline"
					>
						Vetting
					</Link>
				</BreadcrumbItem>
				<BreadcrumbItem last>
					<BreadcrumbCurrent>{formatVettingId(detail.sequence)}</BreadcrumbCurrent>
				</BreadcrumbItem>
			</Breadcrumb>

			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-1">
					<p className="text-field-label text-primary">Title</p>
					<h2 className="text-page-title text-heading min-w-0 break-all">
						{detail.title}
					</h2>
				</div>
				<dl className="flex flex-wrap gap-x-8 gap-y-3">
					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Status of the request</dt>
						<dd className="text-description text-foreground">
							{workStatusLabel(detail.status)}
						</dd>
					</div>

					<div className="flex flex-col gap-1">
						<dt className="text-meta text-muted">Submitted at</dt>
						<dd className="text-description text-foreground tabular-nums">
							{submitted ? formatIstDateTime(submitted) : "—"}
						</dd>
					</div>
					{reviewed ? (
						<div className="flex flex-col gap-1">
							<dt className="text-meta text-muted">Reviewed at</dt>
							<dd className="text-description text-foreground tabular-nums">
								{formatIstDateTime(reviewed)}
							</dd>
						</div>
					) : null}
				</dl>
			</div>

			{/* THE NOTES, IN THE SAME SPOT AND STYLE AS A QUERY'S DESCRIPTION —
			    vetting's equivalent of the case brief. Empty renders nothing,
			    same as query-detail's `description === "" ? null : …`, rather
			    than a placeholder saying so. */}
			{detail.notes === null || detail.notes === "" ? null : (
				<div className="flex flex-col gap-2">
					<p className="text-field-label text-primary">Description</p>
					<div className="border-border bg-surface-sunken border-l-accent rounded-card border border-l-[3px] p-6">
						<ExpandableRichText
							className="max-w-[80ch]"
							html={detail.notes}
						/>
					</div>
				</div>
			)}

			{documents.length > 0 ? (
				<AttachmentList
					attachments={documents}
					variant="thread"
					vettingId={vettingId}
				/>
			) : null}

			<div className="flex flex-col gap-8 lg:pt-2">
				<div className="flex min-w-0 flex-col gap-6">
					<section
						className="flex min-w-0 flex-col gap-3"
						aria-label="Conversation"
					>
						{detail.messages.length > 0 ? (
							<VettingThread
								messages={detail.messages}
								vettingId={detail.id}
							/>
						) : (
							<div className="border-border-strong flex flex-col items-center gap-3 rounded-lg border border-dashed px-5 py-6 text-center">
								<p className="text-description text-muted">
									No replies yet. An expert will respond to your query here.
								</p>
							</div>
						)}
					</section>

					{/* 'Was this helpful?' — shown while the request is Answered and
					    unrated (feedbackDue) or as a read-only summary once rated.
					    Submitting closes the request server-side. */}
					{detail.feedbackDue || detail.feedback ? (
						<VettingFeedbackPrompt
							vettingId={detail.id}
							title={detail.title}
							feedback={detail.feedback}
						/>
					) : null}

					{detail.canFollowUp ? (
						<details className="group border-border bg-surface flex flex-col gap-2 rounded-lg border p-5 shadow-sm">
							{/* AN ACCORDION, CLOSED UNTIL ASKED FOR (client feedback: the box
							    was very big). The composer stays mounted while closed, so a
							    half-written follow-up is not lost by folding it away. */}
							<summary className="flex cursor-pointer list-none items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
								<h3 className="text-card-title text-foreground text-primary">
									Add a follow-up
								</h3>
								<svg
									className="text-muted size-5 shrink-0 transition-transform group-open:rotate-180"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="m6 9 6 6 6-6" />
								</svg>
							</summary>
							<div className="mt-2 flex flex-col gap-2">
								<p className="text-meta text-muted">
									Your expert is notified on WhatsApp. Everything you add stays on
									this thread.
								</p>
								<div className="border-info-border bg-info-soft text-meta text-foreground flex items-start gap-3 rounded-md border p-4">
									<InfoIcon
										className="text-info mt-0.5 size-4 shrink-0"
										aria-hidden="true"
									/>
									<span>
										Title, category, notes and the original documents cannot be
										edited. If you uploaded the wrong file, add anything new as
										a follow-up or create a new vetting request.
									</span>
								</div>
								<VettingFollowUpComposer vettingId={detail.id} />
							</div>
						</details>
					) : (
						<div className="border-border-strong bg-surface-sunken text-description text-secondary-foreground flex flex-col items-center gap-3 rounded-lg border border-dashed p-6 text-center">
							<p className="max-w-[var(--measure-prose-narrow)]">
								This request is closed. Raise a new vetting request if you need more
								help with this notice.
							</p>
						</div>
					)}

					{detail.status === "answered" || detail.status === "closed" ? (
						<div className="rounded-card border-border bg-subtle border p-4">
							<p className="text-meta text-muted">
								When you file: respond through e-Proceedings → e-Assessment /
								Proceedings on the income-tax portal rather than by email, and keep
								the acknowledgement number. If the CPC replies with a further
								adjustment, raise a new vetting request and attach that reply.
							</p>
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}
